// The engagement at a glance as the engine computed it (`engagements.status`, engagement.overview, UI_DATA §8.3): the risk
// summary, the open before-release items, and the deadlines with the engine's days left and status. Nothing is computed here:
// a deadline's days and status, the next deadline and the risk counts are the engine's; the report due date is recorded by a
// manager from the engagement letter in the vault (engagement.deadline), never assumed.
import { useState, type FormEvent } from "react";
import { useApp } from "../../app/context";
import { useEngine } from "../../app/engine";
import { Button, DocView, Drawer, ErrorState, Field, StatusPill, inputCls } from "../../components";
import { newIdempotencyKey } from "../../data/source";
import type { Deadline, EngagementHeader, RiskSummary } from "../../data/types";
import { mayRequest, validatePayload } from "../../lib/commands";
import { fmtDate, fmtStamp, plural, titleCase } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { useReadRequest } from "./shared";

const WHAT: Record<string, string> = { report_due: "Report due", assembly: "File assembly", fac: "FAC submission" };
export const deadlineName = (what: string) => WHAT[what] ?? titleCase(what);
/** The engine's days figure in words (formatting only; the number and the status are the engine's). */
export const daysText = (d: Pick<Deadline, "days" | "status">) =>
  d.days === null ? "" : d.status === "met" ? "" : d.days < 0 ? `${plural(-d.days, "day")} late` : d.days === 0 ? "due today" : `in ${plural(d.days, "day")}`;

/** A deadline in one line: the engine's status pill, what, the due date and the days left. */
export function DeadlineLine({ d }: { d: Deadline }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <StatusPill state={d.status} />
      <span className="font-medium">{deadlineName(d.what)}</span>
      {d.due ? <span>{fmtDate(d.due)}</span> : <span className="text-muted">date not known</span>}
      {daysText(d) && <span className="text-xs text-muted">{daysText(d)}</span>}
    </span>
  );
}

export function RiskSummaryView({ risk }: { risk: RiskSummary }) {
  return (
    <div className="space-y-2 text-sm">
      <p className="flex flex-wrap items-center gap-1.5">
        <StatusPill state={risk.significant > 0 ? "blocked" : "neutral"} label={`${risk.significant} significant`} />
        <StatusPill state={risk.fraud > 0 ? "blocked" : "neutral"} label={`${risk.fraud} fraud`} />
        <span className="text-xs text-muted">{plural(risk.assessed, "assessment")} · RMM {["H", "M", "L"].map((k) => `${k} ${risk.byRmm[k] ?? 0}`).join(" · ")}</span>
      </p>
      {risk.highest.length > 0 && (
        <table className="w-full text-xs" aria-label="Highest risks of material misstatement">
          <thead className="text-left text-muted"><tr><th className="py-0.5 pr-2 font-medium">Area</th><th className="py-0.5 pr-2 font-medium">RMM</th><th className="py-0.5 font-medium">Flags</th></tr></thead>
          <tbody className="divide-y divide-line">
            {risk.highest.map((h) => (
              <tr key={h.area}><td className="py-1 pr-2">{titleCase(h.area)}</td><td className="py-1 pr-2 font-mono">{h.rmm}</td>
                <td className="py-1">{[h.significant && "significant", h.fraud && "fraud"].filter(Boolean).join(", ") || "—"}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      {risk.source && <p className="text-[11px] text-muted">Source: {risk.source}</p>}
    </div>
  );
}

export function DeadlineList({ deadlines }: { deadlines: Deadline[] }) {
  if (!deadlines.length) return <p className="text-sm text-muted">No deadline published.</p>;
  return (
    <ul className="space-y-2 text-sm" aria-label="Deadlines">
      {deadlines.map((d) => (
        <li key={d.what}>
          <DeadlineLine d={d} />
          {d.missing && <span className="block text-xs text-muted">{d.missing}</span>}
          {d.source && <span className="block text-xs text-muted">From <span className="font-mono">{d.source.cite.slice(0, 16)}…</span>{d.source.locator ? ` · ${d.source.locator}` : ""}{d.source.by ? ` · recorded by ${d.source.by}` : ""}{d.source.at ? ` ${fmtStamp(d.source.at)}` : ""}</span>}
          {d.rule && <span className="block text-[11px] text-muted">{d.rule}</span>}
          {d.metOn && <span className="block text-xs">Met on {fmtDate(d.metOn)}</span>}
        </li>
      ))}
    </ul>
  );
}

export function BeforeReleaseItems({ h }: { h: EngagementHeader }) {
  if (!h.beforeReleaseItems.length) return <p className="text-sm text-muted">{h.counts.beforeRelease ? `${plural(h.counts.beforeRelease, "before-release item")} (the engine has not published their text).` : "No open before-release item."}</p>;
  return (
    <div>
      <ul className="space-y-1 text-sm" aria-label="Before-release items">
        {h.beforeReleaseItems.map((b) => <li key={b.id} className="flex gap-2"><span className="font-mono text-xs text-muted">#{b.id}</span><span className="min-w-0 flex-1">{b.text}</span></li>)}
      </ul>
      {h.counts.beforeRelease > h.beforeReleaseItems.length && <p className="mt-1 text-xs text-muted">{h.counts.beforeRelease - h.beforeReleaseItems.length} more (engine count {h.counts.beforeRelease}).</p>}
    </div>
  );
}

/** Whether the engine published the overview fields on this engagement's header. */
export const hasOverview = (h: EngagementHeader) => h.overviewAsOf !== null || h.overviewError !== null || h.deadlines.length > 0 || h.risk !== null;

/** Where the header lacks the overview (an older publish), ask the engine for it with a read request (engagement.overview). */
export function AskOverview() {
  const read = useReadRequest();
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted">The engine has not published the overview (risk, deadlines, before-release items) with this engagement.</p>
      <Button size="sm" onClick={() => void read.ask("engagement.overview", {}, "Engagement overview")}>Ask the engine for the overview</Button>
      {read.id && read.result == null && !read.error && <p className="text-xs text-muted">Asked the engine — the overview shows here when it has run the read.</p>}
      {read.error && <p className="text-xs text-blocked">{read.error}</p>}
      {read.result != null && <div className="max-h-80 overflow-auto rounded border border-line p-2"><DocView doc={read.result} /></div>}
    </div>
  );
}

/** The report due date, recorded by a manager from a document in the vault (engagement.deadline --kind report_due). */
export function DeadlineRecorder({ engagementId, current }: { engagementId: string; current: Deadline | undefined }) {
  const { catalogue, me } = useApp();
  const allowed = mayRequest(catalogue.get("engagement.deadline"), me, "engagement");
  const [open, setOpen] = useState(false);
  const label = current?.due ? "Change the report due date…" : "Record the report due date…";
  return (
    <>
      <Button size="sm" disabled={!allowed.ok} title={allowed.reason ?? "engagement.deadline — from the engagement letter in the vault"}
        aria-label={`${label}${allowed.ok ? "" : ` (not available: ${allowed.reason})`}`} onClick={() => setOpen(true)}>{label}</Button>
      {!allowed.ok && <p className="mt-1 text-xs text-muted">{allowed.reason}</p>}
      <Drawer open={open} onClose={() => setOpen(false)} title="Record the report due date">
        {open && <DeadlineForm engagementId={engagementId} onDone={() => setOpen(false)} />}
      </Drawer>
    </>
  );
}

function DeadlineForm({ engagementId, onDone }: { engagementId: string; onDone: () => void }) {
  const { ds, catalogue } = useApp();
  const request = useEngine();
  const docs = useLoad(() => ds.table(engagementId, "documents"), [ds, engagementId]);
  const [key] = useState(newIdempotencyKey);
  const [v, setV] = useState({ due: "", source_doc: "", locator: "", note: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const spec = catalogue.get("engagement.deadline");
  const vault = (docs.data ?? []).filter((d) => !d.supersededAt);
  if (!spec) return <ErrorState error="engagement.deadline is not in the engine's command catalogue." />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload: Record<string, unknown> = { kind: "report_due", due: v.due, source_doc: v.source_doc, locator: v.locator.trim(), ...(v.note.trim() ? { note: v.note.trim() } : {}) };
    const errs = validatePayload(spec, payload);
    for (const k of ["due", "source_doc", "locator"]) if (!payload[k]) errs[k] = "Required.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    const row = await request({ command: "engagement.deadline", engagementId, payload, what: "Record the report due date", idempotencyKey: key });
    setBusy(false);
    if (row) onDone();
  };
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV((s) => ({ ...s, [k]: e.target.value }));
  return (
    <form onSubmit={submit} noValidate className="space-y-3" aria-label="Record the report due date">
      <p className="font-mono text-xs text-muted">engagement.deadline — {spec.summary}</p>
      <p className="text-sm text-muted">The due date is the one the firm promised in the engagement letter: pick that document in the vault and say where it says so. The engine stamps who recorded it and when; nothing is assumed.</p>
      <Field id="dl-doc" label="Document in the vault" error={errors.source_doc} required hint="The engagement letter (or the document that sets the date).">
        <select id="dl-doc" className={inputCls} value={v.source_doc} onChange={set("source_doc")} aria-required aria-invalid={!!errors.source_doc || undefined}>
          <option value="">{docs.loading ? "Loading the vault…" : vault.length ? "Choose…" : "No document in the vault yet"}</option>
          {vault.map((d) => <option key={d.sha256} value={d.sha256}>{d.originalName} · {d.sha256.slice(0, 12)}</option>)}
        </select>
      </Field>
      <Field id="dl-due" label="Due" error={errors.due} required>
        <input id="dl-due" type="date" className={inputCls} value={v.due} onChange={set("due")} aria-required aria-invalid={!!errors.due || undefined} />
      </Field>
      <Field id="dl-loc" label="Where in the document" error={errors.locator} required hint="Section, page or sentence (e.g. Timing, first sentence).">
        <input id="dl-loc" className={inputCls} value={v.locator} onChange={set("locator")} aria-required aria-invalid={!!errors.locator || undefined} />
      </Field>
      <Field id="dl-note" label="Note (optional)" error={errors.note}>
        <textarea id="dl-note" className={`${inputCls} min-h-[60px]`} value={v.note} onChange={set("note")} />
      </Field>
      {docs.error && <ErrorState error={docs.error} />}
      <div className="flex justify-end gap-2"><Button onClick={onDone}>Cancel</Button><Button kind="primary" type="submit" disabled={busy}>Send to the engine</Button></div>
    </form>
  );
}
